"use client";

import {
  type CrhQuestionDocument,
  typesenseSearch,
} from "../../../actions/typesenseSearch";
import { useContactPageRouterContext } from "../../../helpers/ContactPageRouterContext";
import { useCrhClickCountingContext } from "../../../helpers/CrhClickCountingContext";
import { useSearchActive } from "../../../helpers/SearchActiveContext";
import { getTypesenseCollectionName } from "../../../helpers/getTypesenseCollectionName";
import { locales } from "../../../helpers/locales.mjs";
import { TagsList } from "../TagsList/TagsList";
import {
  trackCrhSearchSeeAllClick,
  trackCrhSearchSuggestionClick,
  useTypesenseSearchAnalytics,
} from "./typesenseSearchAnalytics";
import { createAutocomplete } from "@algolia/autocomplete-core";
import type { AutocompleteState } from "@algolia/autocomplete-core";
import {
  CosmosButton,
  CosmosIconArrowLeft,
  CosmosIconClose,
  CosmosIconSearch,
  CosmosText,
} from "@cosmos/web-react";
import classnames from "classnames/bind";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactModal from "react-modal";
import {
  SearchResponse,
  SearchResponseHit,
} from "typesense/lib/Typesense/Documents";
import styles from "./TypesenseSearchModal.module.css";

const cx = classnames.bind(styles);

/** Minimum characters before the suggestion panel opens and Typesense is queried. */
const TYPESENSE_SEARCH_MIN_QUERY_LENGTH = 3;

function topicLabelsFromDocument(
  topics: CrhQuestionDocument["crh_topics"],
): string[] {
  if (!topics?.length) return [];
  return topics.map((t) => t.label).filter(Boolean);
}

type AutocompleteItem = {
  document: CrhQuestionDocument;
  highlights: SearchResponseHit<CrhQuestionDocument>["highlights"];
};

export type TypesenseSearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Notified whenever the autocomplete input value changes, so callers can
   * mirror it (e.g. on a closed-state trigger button).
   */
  onQueryChange?: (query: string) => void;
  localeString: string;
  searchInputLabel: string;
  searchInputPlaceholderLabel: string;
  searchInputClearLabel?: string;
  searchSeeAllResultsButtonLabel: string;
  searchNoResultsLabel: string;
  searchModalCloseLabel: string;
  // closeButtonLabel: string;
};

export function TypesenseSearchModal({
  isOpen,
  onClose,
  onQueryChange,
  localeString,
  searchInputLabel = "Search questions",
  searchInputPlaceholderLabel = "Search all FAQs",
  searchInputClearLabel = "Clear search",
  searchModalCloseLabel = "Close search",
  searchSeeAllResultsButtonLabel = "See all results",
  searchNoResultsLabel = "No matching questions found",
  // closeButtonLabel = "Close",
}: TypesenseSearchModalProps) {
  const router = useRouter();
  const contactPageRouter = useContactPageRouterContext();
  const { selectedSearchQuery, selectSearchUrl, deselectSearchUrl } =
    contactPageRouter;
  const { setSearchActive, registerSearchModalOpen } = useSearchActive();
  const { trackClick } = useCrhClickCountingContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Needed so the first render after mount can spread `getInputProps` with a real element. */
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  /** Submitted query waiting for the URL to update — avoids syncing back to a stale `?q=`. */
  const pendingSubmitQueryRef = useRef<string | null>(null);

  const assignInputRef = useCallback((el: HTMLInputElement | null) => {
    inputRef.current = el;
    setInputEl(el);
  }, []);

  const collection = getTypesenseCollectionName(localeString);
  const lang =
    locales.find((l) => l.localeString === localeString)?.lang ?? "en";

  const searchTypesense = useCallback(
    async (
      query: string,
      perPage: number,
    ): Promise<SearchResponse<CrhQuestionDocument>> => {
      try {
        const results = await typesenseSearch({
          collection,
          query,
          type: "hybrid",
          perPage,
          lang,
        });
        return results;
      } catch (error) {
        throw new Error("Typesense search failed", { cause: error });
      }
    },
    [collection, lang],
  );

  // Mutable refs so the autocomplete instance (created once) can read the
  // latest values without being recreated.
  const callbacksRef = useRef({
    searchTypesense,
    router,
    contactPageRouter,
    selectSearchUrl,
    deselectSearchUrl,
    setSearchActive,
    onClose,
    trackClick,
  });
  useEffect(() => {
    callbacksRef.current = {
      searchTypesense,
      router,
      contactPageRouter,
      selectSearchUrl,
      deselectSearchUrl,
      setSearchActive,
      onClose,
      trackClick,
    };
  });

  const [autocompleteState, setAutocompleteState] = useState<
    AutocompleteState<AutocompleteItem>
  >({
    activeItemId: null,
    query: "",
    completion: null,
    collections: [],
    isOpen: false,
    status: "idle",
    context: {},
  });

  useTypesenseSearchAnalytics({
    isOpen,
    autocompleteState,
    minQueryLength: TYPESENSE_SEARCH_MIN_QUERY_LENGTH,
  });

  const autocomplete = useMemo(
    () =>
      createAutocomplete<AutocompleteItem>({
        placeholder: searchInputPlaceholderLabel,
        initialState: { query: "" },
        shouldPanelOpen({ state }) {
          return state.query.trim().length >= TYPESENSE_SEARCH_MIN_QUERY_LENGTH;
        },
        onStateChange({ state }) {
          setAutocompleteState(state);
        },
        onSubmit({ state }) {
          const {
            router,
            selectSearchUrl,
            deselectSearchUrl,
            setSearchActive,
            onClose,
          } = callbacksRef.current;
          const query = state.query.trim();
          const suggestionItems = state.collections.flatMap(
            (collection) => collection.items,
          ); // For Analytics
          if (query) {
            trackCrhSearchSeeAllClick(query, suggestionItems);
            pendingSubmitQueryRef.current = query;
            router.push(selectSearchUrl({ query }));
          } else {
            setSearchActive(false);
            router.push(deselectSearchUrl());
          }
          onClose();
        },
        getSources({ query }) {
          if (
            !query ||
            query.trim().length < TYPESENSE_SEARCH_MIN_QUERY_LENGTH
          ) {
            return [];
          }
          return [
            {
              sourceId: "typesense",
              async getItems() {
                try {
                  const results = await callbacksRef.current.searchTypesense(
                    query,
                    3,
                  );
                  return (
                    results.hits?.map((hit) => ({
                      document: hit.document,
                      highlights: hit.highlights,
                    })) ?? []
                  );
                } catch {
                  return [];
                }
              },
              onSelect({
                item,
                state,
              }: {
                item: AutocompleteItem;
                state: AutocompleteState<AutocompleteItem>;
              }) {
                const { router, contactPageRouter, onClose, trackClick } =
                  callbacksRef.current;
                const suggestionItems = state.collections.flatMap(
                  (collection) => collection.items,
                );

                // const tags = topicLabelsFromDocument(item.document.crh_topics);
                trackClick();
                trackCrhSearchSuggestionClick(item, suggestionItems);

                // analyticsClick({
                //   value: `support hub search | ${questionTitleFromDocument(item.document)} | ${tags.join(", ")}`,
                //   clickUrl: null,
                //   elementPosition: "search modal",
                //   method: "Outbound: FALSE",
                //   contentType: "question",
                // });

                if (item.document?.id) {
                  router.push(
                    contactPageRouter.selectQuestionUrl({
                      uuid: item.document.id,
                    }),
                  );
                  onClose();
                }
              },
            },
          ];
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Sync query when URL-driven selectedSearchQuery changes externally, but
  // only while the modal is closed. While open, the query is owned by the
  // open effect below (which pre-fills the active search term) and the user's
  // own typing, so we don't want this to fight with it.
  useEffect(() => {
    if (isOpen) return;

    const pending = pendingSubmitQueryRef.current;
    const urlQuery = selectedSearchQuery ?? "";

    if (pending !== null && pending !== urlQuery) {
      return;
    }

    if (pending !== null && pending === urlQuery) {
      pendingSubmitQueryRef.current = null;
    }

    autocomplete.setQuery(urlQuery);
    autocomplete.refresh();
  }, [selectedSearchQuery, autocomplete, isOpen]);

  // Open with the active search term (from the URL) pre-filled, so the user
  // can see and refine their current search.
  //
  // NB: the closed-state sync effect above has already fetched results for
  // this exact query while the modal was closed, so we deliberately do NOT
  // call `refresh()` here. Refreshing re-runs the async search, which clears
  // and repopulates the collections and makes the suggestions flicker. We
  // just reuse the results already in state and open the panel directly when
  // the term is long enough (mirrors `shouldPanelOpen`).
  useEffect(() => {
    if (!isOpen) return;
    const activeQuery = selectedSearchQuery ?? "";
    autocomplete.setQuery(activeQuery);
    autocomplete.setIsOpen(activeQuery.trim().length >= 3);
  }, [isOpen, autocomplete, selectedSearchQuery]);

  // --- Previous behaviour: always open with a blank input, even when a
  // --- search was active in the URL. Restore this block (and remove the one
  // --- above) to revert to opening the modal empty.
  // useEffect(() => {
  //   if (!isOpen) return;
  //   autocomplete.setQuery("");
  //   autocomplete.setIsOpen(false);
  //   autocomplete.refresh();
  // }, [isOpen, autocomplete]);

  // Track whether the search is "active" - URL-driven results count too, so
  // clearing the modal input on a search-results page doesn't drop inert state.
  useEffect(() => {
    setSearchActive(
      autocompleteState.query.trim().length > 0 || Boolean(selectedSearchQuery),
    );
  }, [autocompleteState.query, selectedSearchQuery, setSearchActive]);

  // Mirror the live input value out so the closed-state trigger button can
  // display it.
  useEffect(() => {
    onQueryChange?.(autocompleteState.query);
  }, [autocompleteState.query, onQueryChange]);

  useEffect(() => {
    if (!isOpen) return;
    return registerSearchModalOpen();
  }, [isOpen, registerSearchModalOpen]);

  useEffect(() => {
    if (!isOpen) {
      inputRef.current = null;
      setInputEl(null);
    }
  }, [isOpen]);

  // If the field is already filled (e.g. URL-driven query), open the panel once
  // the real `<input>` exists so autocomplete-core can attach to it.
  useEffect(() => {
    if (!isOpen || !inputEl) return;
    if (
      autocompleteState.query.trim().length >= TYPESENSE_SEARCH_MIN_QUERY_LENGTH
    ) {
      autocomplete.setIsOpen(true);
    }
  }, [isOpen, inputEl, autocomplete, autocompleteState.query]);

  // Focus the search input once the modal has fully opened.
  const handleAfterOpen = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  // Mobile: close panel on scroll/touch outside the form/panel area.
  const { getEnvironmentProps } = autocomplete;
  useEffect(() => {
    if (!isOpen) return;
    if (!(formRef.current && panelRef.current && inputEl)) return;

    const { onTouchStart, onTouchMove, onMouseDown } = getEnvironmentProps({
      formElement: formRef.current,
      panelElement: panelRef.current,
      inputElement: inputEl,
    });

    window.addEventListener("touchstart", onTouchStart);
    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("mousedown", onMouseDown);

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("mousedown", onMouseDown);
    };
  }, [getEnvironmentProps, autocompleteState.isOpen, isOpen, inputEl]);

  const handleClearAndClose = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.preventDefault();
    // On a search results page, dismissing the modal must NOT navigate
    // away from the page. Keep the URL (and therefore the active search)
    // intact; the closed-state trigger reads from the URL, not the input.
    if (selectedSearchQuery) {
      autocomplete.setQuery("");
      autocomplete.setIsOpen(false);
      onClose();
      return;
    }
    autocomplete.setQuery("");
    autocomplete.setIsOpen(false);
    setSearchActive(false);
    router.push(deselectSearchUrl());
    onClose();
  };

  const handleClearInput = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.preventDefault();
    autocomplete.setQuery("");
    autocomplete.setIsOpen(false);
    autocomplete.refresh();
    inputRef.current?.focus();
  };

  const handleSeeAllResults = (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    autocomplete.setIsOpen(false);
    const query = autocompleteState.query.trim();
    const suggestionItems = autocompleteState.collections.flatMap(
      (collection) => collection.items,
    ); // For Analytics
    if (query) {
      trackCrhSearchSeeAllClick(query, suggestionItems);
      pendingSubmitQueryRef.current = query;
      router.push(selectSearchUrl({ query }));
    } else {
      setSearchActive(false);
      router.push(deselectSearchUrl());
    }
    onClose();
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClearAndClose}
      onAfterOpen={handleAfterOpen}
      className={cx("modal-layout")}
      portalClassName={cx("modal-portal")}
      overlayClassName={cx("modal-overlay")}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      shouldFocusAfterRender={false}
      shouldReturnFocusAfterClose
      // We apply `inert` ourselves on specific layout regions, so we don't
      // want react-modal toggling `aria-hidden` on `#support-hub-app`.
      ariaHideApp={false}
      // Disable react-modal's `<body>` class management — its ref counter
      // can desync with React lifecycle and leave the class stuck. The body
      // class is driven by `SearchActiveContextProvider` instead.
      bodyOpenClassName={null}
      contentLabel={searchInputLabel}
    >
      <div className={cx("modal")}>
        <div
          className={cx("autocomplete-root")}
          {...autocomplete.getRootProps({})}
        >
          <div className={cx("autocomplete-header")}>
            <CosmosButton
              type="button"
              className={cx("back-button")}
              buttonAriaLabel={searchModalCloseLabel}
              kind="tertiary"
              shape="circle"
              size="small"
              appearance="dark"
              onClick={handleClearAndClose}
            >
              <CosmosIconArrowLeft
                slot="icon"
                className={cx("back-button-icon")}
              />
            </CosmosButton>

            <form
              ref={formRef}
              className={cx("autocomplete-form")}
              autoComplete="off"
              {...(inputEl
                ? (autocomplete.getFormProps({
                    inputElement: inputEl,
                  }) as unknown as React.FormHTMLAttributes<HTMLFormElement>)
                : {})}
            >
              <div className={cx("input-wrapper")}>
                <input
                  ref={assignInputRef}
                  // Controlled defaults for the first render — before the
                  // ref runs, `inputEl` is null so the autocomplete spread
                  // below is empty, and without these the input would be
                  // uncontrolled on render 1 and controlled on render 2,
                  // triggering React's "uncontrolled → controlled" warning.
                  // The autocomplete spread overrides both on render 2.
                  value={autocompleteState.query}
                  onChange={() => {}}
                  {...(inputEl
                    ? (autocomplete.getInputProps({
                        inputElement: inputEl,
                      }) as unknown as React.InputHTMLAttributes<HTMLInputElement>)
                    : {})}
                  className={cx("autocomplete-input")}
                  type="search"
                  enterKeyHint="search"
                  autoComplete="off"
                  aria-label={searchInputLabel}
                  placeholder={searchInputPlaceholderLabel}
                />
                <CosmosButton
                  type="submit"
                  className={cx("submit-icon-button")}
                  buttonAriaLabel={searchInputLabel}
                  kind="primary"
                  shape="rectangle"
                >
                  <CosmosIconSearch />
                </CosmosButton>

                <CosmosButton
                  type="button"
                  className={cx("autocomplete-clear-button")}
                  buttonAriaLabel={searchInputClearLabel}
                  kind="tertiary"
                  shape="circle"
                  size="small"
                  appearance="dark"
                  onClick={handleClearInput}
                  disabled={autocompleteState.query.length <= 0}
                >
                  <CosmosIconClose slot="icon" />
                </CosmosButton>
              </div>
              {/* <CosmosButton
                type="button"
                kind="bare"
                size="small"
                shape="circle"
                className={cx("close-button")}
                buttonAriaLabel={closeButtonLabel}
                onClick={onClose}
              >
                <CosmosIconClose
                  slot="icon"
                  className={cx("close-button-icon")}
                />
              </CosmosButton> */}
            </form>
          </div>

          {autocompleteState.isOpen &&
            autocompleteState.collections.length > 0 && (
              <div
                ref={panelRef}
                data-status={autocompleteState.status}
                className={cx("menu-container")}
                {...(autocomplete.getPanelProps(
                  {},
                ) as unknown as React.HTMLAttributes<HTMLDivElement>)}
              >
                <div className={cx("menu")}>
                  <div className={cx("menu-scroll")}>
                    {autocompleteState.collections.map((collection, index) => {
                      const { source, items } = collection;

                      if (items.length === 0) {
                        return (
                          <div
                            key={`source-${index}`}
                            className={cx("no-results")}
                          >
                            <CosmosText size="small" tag="p">
                              {searchNoResultsLabel}
                            </CosmosText>
                          </div>
                        );
                      }

                      return (
                        <ul
                          key={`source-${index}`}
                          className={cx("menu-group")}
                          {...autocomplete.getListProps()}
                          onMouseLeave={() =>
                            autocomplete.setActiveItemId(null)
                          }
                        >
                          {items.map((item) => {
                            const doc = item.document;
                            const questionHighlight = item.highlights?.find(
                              (h) => h.field === "question",
                            );
                            const summaryHighlight = item.highlights?.find(
                              (h) => h.field === "summary_question",
                            );
                            const displayText = doc.summary_question
                              ? summaryHighlight?.snippet ||
                                doc.summary_question
                              : questionHighlight?.snippet || doc.question;
                            const tags = topicLabelsFromDocument(
                              doc.crh_topics,
                            );
                            const questionHref =
                              contactPageRouter.selectQuestionUrl({
                                uuid: doc.id,
                              });

                            return (
                              <li
                                key={doc.id}
                                className={cx("menu-item")}
                                {...(autocomplete.getItemProps({
                                  item,
                                  source,
                                }) as unknown as React.LiHTMLAttributes<HTMLLIElement>)}
                              >
                                <div className={cx("suggestion-card")}>
                                  <h2 className={cx("suggestion-heading")}>
                                    <Link
                                      href={questionHref}
                                      className={cx("suggestion-link")}
                                      onClick={(event) => {
                                        // Let autocomplete `onSelect` handle
                                        // navigation and `select_promotion`.
                                        event.preventDefault();
                                      }}
                                    >
                                      <CosmosText
                                        tag="span"
                                        size="small"
                                        weight="regular"
                                        appearance="dark"
                                        className={cx("question-text")}
                                      >
                                        <span
                                          dangerouslySetInnerHTML={{
                                            __html: displayText,
                                          }}
                                        />
                                      </CosmosText>
                                    </Link>
                                  </h2>
                                  <div className={cx("suggestion-tags")}>
                                    <TagsList tags={tags} />
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      );
                    })}
                  </div>
                  {autocompleteState.collections.some(
                    (c) => c.items.length > 0,
                  ) && (
                    <div className={cx("menu-group-footer")}>
                      <CosmosButton
                        className={cx("menu-group-footer-button")}
                        kind="primary"
                        size="small"
                        shape="rectangle"
                        onClick={handleSeeAllResults}
                        // width="full"
                      >
                        {searchSeeAllResultsButtonLabel}
                      </CosmosButton>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>
    </ReactModal>
  );
}
